interface TextAreaProps {
  label?: string;
  id?: string;
  isRequired?: boolean;
  maxLength?: number;
  height?: string;
  error?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  withMaxLength?: boolean;
  value?: string;
  name?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  [key: string]: any;
  optionalLabel?: string;
}

const TextArea = ({
  label,
  id,
  isRequired,
  maxLength = 120,
  height = '200px',
  error,
  size = 'md',
  withMaxLength = true,
  optionalLabel = '',
  ...props
}: TextAreaProps) => {
  const value = props.value ?? ''; // safeguard for length counter
  const isOverLimit = withMaxLength && value.length > maxLength;

  const sizeMap = {
    sm: {
      wrapper: 'py-[6px]',
      input: 'py-[6px]',
      leftSection: 'px-2 py-[6px]',
      text: 'text-xs',
    },
    md: {
      wrapper: 'py-[10px]',
      input: 'py-[10px]',
      leftSection: 'px-3 py-[10px]',
      text: 'text-sm md:text-base lg:text-sm 3xl:text-base',
    },
    lg: {
      wrapper: 'py-[14px]',
      input: 'py-[14px]',
      leftSection: 'px-4 py-[14px]',
      text: 'text-base md:text-lg lg:text-base 3xl:text-lg',
    },
    xl: {
      wrapper: 'py-[18px]',
      input: 'py-[18px]',
      leftSection: 'px-5 py-[18px]',
      text: 'text-lg md:text-xl lg:text-lg 3xl:text-xl',
    },
  };

  const currentSize = sizeMap[size] || sizeMap.md;

  return (
    <div className="relative w-full flex flex-col items-start gap-2">
      {/* Label */}
      <label
        htmlFor={id || props.name}
        className="text-left text-xs md:text-sm lg:text-sm 3xl:text-base text-input-active-label font-semibold"
      >
        {label} {isRequired && <span className="text-sm text-error-1">*</span>}{' '}
        {optionalLabel && (
          <span className="text-xs text-error-2 font-normal">
            ({optionalLabel})
          </span>
        )}
      </label>

      {/* Textarea field */}
      <div
        className={`w-full flex items-center rounded-md bg-modal-input transition-all ${error || isOverLimit ? 'border-0.5 border-error-2' : 'border-0.5 border-gray-3/20'}`}
      >
        <textarea
          id={id || props.name}
          style={{ height }}
          {...props}
          className={`placeholder:text-input-inactive-placeholder placeholder:text-sm placeholder:3xl:text-base flex-1 rounded-md ${currentSize.input} px-[14px] py-[10px]  focus:outline-none ${currentSize.text} text-text-primary-highlight bg-transparent border-0 resize-none`}
        />
      </div>
      {/* Character counter - only show if withMaxLength is true */}
      {withMaxLength && (
        <p
          className={`absolute bottom-3 right-5 text-xs ${
            isOverLimit ? 'text-error-2' : 'text-gray-4'
          }`}
        >
          {value.length}/{maxLength}
        </p>
      )}

      {/* Error message */}
      {(error || isOverLimit) && (
        <p className="text-xs text-error-2 mt-1">
          {isOverLimit
            ? `Maximum ${maxLength} characters allowed. You've entered ${value.length}.`
            : error}
        </p>
      )}
    </div>
  );
};

export default TextArea;
